/**
 * In-app clipboard for copying a Doc into another folder.
 * Independent of the system clipboard — text / images / mission cards
 * never count as a copied doc.
 */

export const DOC_CLIPBOARD_EVENT = 'fokus:doc-clipboard-changed';
const STORAGE_KEY = 'fokus:doc-clipboard';

let copied = null;

const untitled = (title) => {
  const name = String(title || '').trim();
  return name || 'Untitled';
};

const readStored = () => {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v?.docId) return null;
    return { docId: v.docId, title: untitled(v.title) };
  } catch {
    return null;
  }
};

const writeStored = (next) => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (next) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore quota / private mode */
  }
};

copied = readStored();

const emit = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(DOC_CLIPBOARD_EVENT, { detail: copied }));
};

export function getCopiedDoc() {
  return copied || readStored();
}

export function copyDocToClipboard(doc) {
  const id = doc?.id || doc?.docId;
  if (!id) return null;
  copied = { docId: id, title: untitled(doc.title) };
  writeStored(copied);
  emit();
  return copied;
}

export function clearCopiedDoc() {
  if (!copied && !readStored()) return;
  copied = null;
  writeStored(null);
  emit();
}

export function copiedDocTitle(title) {
  return `Copy of ${untitled(title)}`;
}

export function ellipsizeTitle(title, max = 36) {
  const name = untitled(title);
  if (name.length <= max) return name;
  return `${name.slice(0, Math.max(1, max - 1))}…`;
}

export function copyDocMenuLabel(title) {
  return `Copy “${ellipsizeTitle(title)}”`;
}

export function pasteDocMenuLabel(title) {
  return `Paste “${ellipsizeTitle(title)}” here`;
}
